"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import dynamicImport from "next/dynamic";
import heart from "@/public/images/heart.svg";
import FaviconIcon from "@/public/Favicon.svg";
import FaviconIconW from "@/public/Favicon w.svg";
import { useTranslate } from "@/components/base/providers/TranslateProv";
import MainSearchComponent from "@/components/shared/SearchAndFilter/MainSearchComponent";
import { useSearchParams } from "next/navigation";
import { useGetAllUniversitiesWithFiltersQuery } from "@/redux/features/api/website/websiteApiSlice";
import UniversityCard from "@/components/shared/UniversityCard";
import { University } from "@/types/university";
import Loader from "@/components/shared/Loader";
import Pagination from "@/components/shared/Pagination";
import SearchNotFound from "@/components/shared/SearchNotFound";
import MapBlue from "@/public/icons/MapBlue.svg";
import { extractLatLng } from "@/utils/extractLatLng";

// Dynamically import the Map component with ssr disabled
const MapWithSearch = dynamicImport(
  () => import("@/components/Home/Map"),
  { ssr: false } // This ensures the component only loads on the client side
);

const UniversitiesClient = () => {
  const { locale } = useTranslate();
  const lang = locale as "ar" | "en";
  const [isMap, setIsMap] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState<string | null>("2");
  const [isMounted, setIsMounted] = useState(false);
  const itemsPerPage = 9;

  const searchParams = useSearchParams();
  function useGetAllSearchParams() {
    const params: { [key: string]: string } = {};

    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return params;
  }

  const { refetch, data, isLoading, error, isFetching } =
    useGetAllUniversitiesWithFiltersQuery({
      ...useGetAllSearchParams(),
      page: currentPage,
      limit: itemsPerPage,
      nominate_it: filter === "recommended" ? 1 : filter === "2" ? "2" : "0",
    });

  const translations = {
    ar: {
      title: "اكتشف أفضل الجامعات والكليات والتخصصات المتاحة.",
      subtitle:
        "ابحث عن الجامعات والكليات والتخصصات التي تلبي طموحاتك الأكاديمية.",
      searchResults: "نتائج البحث",
      favorites: "المفضلة",
      recommended: "نرشحها",
      map: "الخريطة",
    },
    en: {
      title: "Join a Rich Journey of Studying Abroad!",
      subtitle:
        "Search for the best universities and language institutes in the world, review them, and apply.",
      searchResults: "Search Results",
      favorites: "Favorites",
      recommended: "Recommended",
      map: "Map",
    },
  };

  const t = translations[lang];

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected + 1);
    refetch();
  };

  const handleFilterClick = () => {
    const newFilter = filter === "recommended" ? "2" : "recommended";
    setFilter(newFilter);
    refetch();
  };

  // Handle mounting state
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (searchParams.get("recommended")) {
      setFilter("recommended");
    }
  }, []);

  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentPage, filter]);

  // Only process map locations if we're mounted and have data
  const mapLocations =
    isMounted &&
    data?.data?.data?.map((university: any) => {
      const mapLink = university?.map_links;
      const coordinates = extractLatLng(mapLink?.link);
      return {
        name: university?.name.ar,
        lat: coordinates?.lat,
        lng: coordinates?.lng,
        details: {
          name: university?.name,
          mapTitle: mapLink?.title,
          image: university?.image,
          id: university?.id,
        },
      };
    });

  // Don't render anything until mounted
  if (!isMounted) {
    return null;
  }

  return (
    <>
      <div
        className="bg-primaryColor w-[95%] mx-auto max-w-[1520px] rounded-[25px] mt-[18px] pt-10 pb-32 flex flex-col gap-5 mb-36"
        ref={resultsRef}
      >
        <p className="text-36 text-white font-[500] mb-5 text-center">
          {t.title}
        </p>
        <p className="text-18 text-white font-[500] mb-3 text-center">
          {t.subtitle}
        </p>
        <div className="mx-auto -mb-[13rem]">
          <MainSearchComponent />
        </div>
      </div>
      <div className="container">
        <div className="flex items-center justify-between gap-5">
          <p className="text-20 text-black font-[500]">
            {t.searchResults} :{" "}
            <span className="text-primaryColor">
              {" "}
              ({data?.data?.data.length ? data?.data?.data.length : 0}){" "}
            </span>
          </p>
          <div className="flex items-center gap-5">
            <button
              onClick={() => setIsMap(!isMap)}
              className={`text-14 flex items-center gap-3 border rounded-[10px] py-2 px-7 ${
                isMap
                  ? "bg-primaryColor text-white"
                  : "border-[#F2F2F2] text-descriptionText"
              }`}
            >
              {t.map}
              <Image src={MapBlue} alt="map" />
            </button>
            <button
              onClick={handleFilterClick}
              className={`text-14 flex items-center gap-3 border rounded-[10px] py-2 px-7 ${
                filter === "recommended"
                  ? "bg-primaryColor text-white"
                  : "border-[#F2F2F2] text-descriptionText"
              }`}
            >
              {t.recommended}
              {filter === "recommended" ? (
                <Image src={FaviconIconW} alt="favicon" width={20} height={20} />
              ) : (
                <Image src={FaviconIcon} alt="favicon" width={20} height={20} />
              )}
            </button>
          </div>
        </div>

        {(isLoading || isFetching) && (
          <div className="flex justify-center items-center h-[300px]">
            <Loader />
          </div>
        )}
        {error && (
          <div className="flex justify-center items-center h-[300px]">
            <p className="text-center text-20 text-red-500 font-[600]">
              {lang === "ar" ? "خطأ في جلب البيانات." : "Error fetching data."}
            </p>
          </div>
        )}
        {!isLoading &&
          !isFetching &&
          !error &&
          (data?.data?.data.length > 0 ? (
            isMap && mapLocations ? (
              <div className="my-5">
                <MapWithSearch locations={mapLocations} />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 my-5">
                {data?.data?.data?.map((item: University, index: number) => (
                  <UniversityCard key={index} locale={locale} item={item} />
                ))}
              </div>
            )
          ) : (
            <SearchNotFound />
          ))}
      </div>
      {!isMap && data?.data?.paginate?.totalPages > 1 && (
        <Pagination
          pageCount={data?.data?.paginate?.totalPages}
          onPageChange={handlePageChange}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default UniversitiesClient;
